import React, { useEffect, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, Input } from '@chapche/ui';
import { propertySaveService } from '../../../services/propertySaveService';
import { LOCALE_RU } from '../../../constant';
import LocaleAffixedField from '../../../shared/LocaleAffixedField';

type PropertySynonym = {
    id: string;
    locale: string;
    synonym: string;
};

type PropertyCanonView = {
    synonyms?: PropertySynonym[];
};

const readCanon = (): { synonyms: PropertySynonym[] } => {
    const item = propertySaveService.getProperty() as PropertyCanonView;
    return {
        synonyms: item.synonyms ?? [],
    };
};

const PropertyCanonLists = () => {
    const { i18n } = useTranslation();
    const [synonyms, setSynonyms] = useState<PropertySynonym[]>([]);
    const [synonymLocale, setSynonymLocale] = useState(LOCALE_RU);
    const [synonymText, setSynonymText] = useState('');

    const refresh = () => {
        const item = readCanon();
        setSynonyms(item.synonyms);
    };

    useEffect(() => {
        refresh();
        propertySaveService.registerItemUpdateObserver(refresh);
        return () => propertySaveService.unRegisterItemUpdateObserver(refresh);
    }, []);

    const submitSynonym = () => {
        const text = synonymText.trim();
        if (!text) {
            return;
        }
        propertySaveService.addSynonym(synonymLocale, text);
        setSynonymText('');
    };

    return (
        <label className="block__text product__form-label">
            <span className="product__form-label_text">
                {i18n.t('app.page.product.propertyItem.synonyms')}
            </span>
            {synonyms.length > 0 ? (
                <ul className="product__forms-list m-0 flex list-none flex-col gap-8 p-0">
                    {synonyms.map((it) => (
                        <li
                            key={it.id}
                            className="flex items-center gap-8 rounded-input border border-solid border-light-grey bg-white px-12 py-8"
                        >
                            <span className="shrink-0 font-montserrat-medium text-sm uppercase text-ink">
                                {it.locale}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-ink">{it.synonym}</span>
                            <button
                                type="button"
                                className="button shrink-0"
                                onClick={() => propertySaveService.deleteSynonym(it.id)}
                            >
                                {i18n.t('app.page.product.close')}
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
            <LocaleAffixedField
                locale={synonymLocale}
                onLocaleChange={setSynonymLocale}
            >
                <Input
                    className={cn(
                        'main__subtitle product__form-input product__form-title mt-0',
                        'box-border w-full max-w-none rounded-input border border-solid border-light-grey',
                        'bg-white px-16 py-12 pr-80 font-montserrat-medium text-xl text-ink',
                        'placeholder:text-secondary-light-grey',
                    )}
                    value={synonymText}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSynonymText(e.target.value)}
                    onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            submitSynonym();
                        }
                    }}
                />
            </LocaleAffixedField>
            <button
                type="button"
                className="button"
                onClick={submitSynonym}
            >
                {i18n.t('app.page.product.add')}
            </button>
        </label>
    );
};

export default PropertyCanonLists;
